/**
 * Schemas for the `timeline.*` tRPC procedures.
 *
 * Cursor-paginated, sorted by `occurredAt desc`. Title and body are
 * locale-resolved server-side using `resolveLocaleField`. The first page
 * (no cursor) is cached for 60s; subsequent pages skip the cache because
 * cursor-keyed entries multiply quickly and the value is small.
 */

import { z } from "zod";

export const TIMELINE_SEVERITIES = [
  "info",
  "milestone",
  "warning",
  "urgent",
] as const;
export const timelineSeveritySchema = z.enum(TIMELINE_SEVERITIES);
export type TimelineSeverity = z.infer<typeof timelineSeveritySchema>;

export const timelineEventSchema = z.object({
  id: z.number().int(),
  outbreakId: z.number().int(),
  occurredAt: z.date(),
  /** Locale-resolved title. */
  title: z.string(),
  /** Locale-resolved body (markdown). */
  body: z.string().nullable(),
  severity: timelineSeveritySchema,
  tags: z.array(z.string()).nullable(),
  newsItemId: z.number().int().nullable(),
  /** Source URL from the linked news item (null when no news link). */
  sourceUrl: z.string().url().nullable(),
  /** News source name (e.g. "WHO", "CDC") for citation display. */
  sourceLabel: z.string().nullable(),
  /** When the news item was fetched — serves as the verification timestamp. */
  verifiedAt: z.date().nullable(),
});
export type TimelineEventOut = z.infer<typeof timelineEventSchema>;

export const timelineListInputSchema = z.object({
  slug: z.string().min(1).max(100),
  cursor: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(100).default(30),
});
export type TimelineListInput = z.infer<typeof timelineListInputSchema>;

export const timelineListOutputSchema = z.object({
  events: z.array(timelineEventSchema),
  nextCursor: z.number().int().min(0).nullable(),
});
export type TimelineListOutput = z.infer<typeof timelineListOutputSchema>;
